import { HttpClient } from '@angular/common/http';
import { Injector, Provider } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { DialogService } from 'primeng/dynamicdialog';
import type { ISettingsRepository } from '@app/domain/ports';
import { SETTINGS_REPOSITORY } from '@app/platform/tokens';
import {
  BANK_INTEGRATION_PLUGINS,
  BANK_STATEMENT_CACHE_PORT,
  type BankIntegrationPlugin,
  type BankStatementCachePort,
  PLUGIN_TRANSLATION_ASSETS,
} from '@app/ui/shared';
import { MonobankApiService } from './monobank-api.service';
import { MonobankSettingComponent } from './ui/monobank-setting.component';
import { MonobankSettingsService } from './monobank-settings.service';
import { MonobankApiConfig, MONOBANK_API_CONFIG } from './tokens';

/**
 * Registers the Monobank integration as an app plugin.
 *
 * - Adds `MonobankSettingComponent` to the Settings "Integrations" section via `BANK_INTEGRATION_PLUGINS`.
 * - Adds Monobank to the dedicated bank import selection page (visible only when Monobank is enabled).
 *
 * @example
 * ```typescript
 * // app.config.ts
 * export const appConfig: ApplicationConfig = {
 *   providers: [
 *     ...provideMonobankPlugin({
 *       apiUrl: environment.monobankApiUrl,
 *       rateLimitMs: environment.monobankRateLimitMs,
 *     }),
 *   ],
 * };
 * ```
 */
export function provideMonobankPlugin(apiConfig: MonobankApiConfig): Provider[] {
  return [
    // ── App-level service bindings ─────────────────────────────────────────────
    { provide: MONOBANK_API_CONFIG, useValue: apiConfig },
    {
      provide: MonobankSettingsService,
      useFactory: (settings: ISettingsRepository, injector: Injector) =>
        new MonobankSettingsService(settings, injector),
      deps: [SETTINGS_REPOSITORY, Injector],
    },
    {
      provide: MonobankApiService,
      useFactory: (http: HttpClient, bankCache: BankStatementCachePort, config: MonobankApiConfig) =>
        new MonobankApiService(http, bankCache, config),
      deps: [HttpClient, BANK_STATEMENT_CACHE_PORT, MONOBANK_API_CONFIG],
    },

    // ── Plugin registrations ───────────────────────────────────────────────────

    {
      provide: PLUGIN_TRANSLATION_ASSETS,
      useValue: {
        pluginId: 'monobank',
        basePath: './assets/i18n/plugins/monobank',
        locales: ['en', 'pl', 'ru', 'uk'],
      },
      multi: true,
    },

    // Dedicated bank integration registration
    {
      provide: BANK_INTEGRATION_PLUGINS,
      useFactory: (
        translate: TranslateService,
        state: MonobankSettingsService,
        dialogService: DialogService,
      ): BankIntegrationPlugin => ({
        id: 'monobank',
        order: 100,
        bankName: () => translate.instant('screens.monobank.labels.bank_name'),
        logoIcon: '/assets/images/banks/monobank.svg',
        settingsComponent: MonobankSettingComponent,
        enabled: () => state.monobankEnabled(),
        openImport: () => {
          void import('./ui/monobank-import-dialog.component').then(({ MonobankImportDialogComponent }) => {
            dialogService.open(MonobankImportDialogComponent, {
              header: translate.instant('screens.monobank.titles.import_dialog'),
              styleClass: 'p-dialog-form p-dialog-maximized',
              modal: true,
              closable: true,
              focusOnShow: false,
            });
          });
        },
      }),
      deps: [TranslateService, MonobankSettingsService, DialogService],
      multi: true,
    },
  ];
}
